import { z } from "zod";
/**
 * The person's own data about one exercise (`D200`): a pinned note, a
 * favourite and a soft preference, and goals. Every OUTPUT field added later
 * is optional (`D172`); every input is strict, so a key an older server does
 * not know is a 422 the outbox quarantines rather than a field silently
 * dropped.
 */
// ── The pinned note ─────────────────────────────────────────────────────────
/**
 * A note is a line read between sets — "banco no furo 4, pegada no anel" —
 * not a diary. Restated by `@repo/training`'s `EXERCISE_NOTE_MAX` and held
 * equal by its `contract-vocabulary.test.ts`.
 */
export const EXERCISE_NOTE_MAX = 280;
export const exerciseNoteView = z.object({
    exerciseId: z.string().min(1),
    /** Trimmed, never empty: a cleared note is no row at all. */
    body: z.string().min(1).max(EXERCISE_NOTE_MAX),
    updatedAt: z.iso.datetime(),
});
/** Every note the person has — small by nature, read whole. */
export const exerciseNoteListOutput = z.object({
    notes: z.array(exerciseNoteView),
});
/** `PUT /me/exercise-notes/:exerciseId` — the whole note, last write wins. */
export const putExerciseNoteInput = z.strictObject({
    body: z.string().min(1).max(EXERCISE_NOTE_MAX),
});
// ── Favourite and preference ────────────────────────────────────────────────
/**
 * The soft half of "I don't want this one" (`D200`). `prefer` ranks an
 * exercise ahead of its equals in selection and in the swap feeds, `avoid`
 * behind them; neither removes anything — the exclusion stays the hard never.
 */
export const EXERCISE_PREFERENCES = ["prefer", "avoid"];
export const exercisePreference = z.enum(EXERCISE_PREFERENCES);
/**
 * The bound on the rows one person may hold — read on every generate and
 * every swap feed (`T-6`). Restated by `@repo/training`.
 */
export const MAX_EXERCISE_PREFERENCES = 500;
export const exercisePreferenceView = z.object({
    exerciseId: z.string().min(1),
    favorite: z.boolean(),
    preference: exercisePreference.nullable(),
    updatedAt: z.iso.datetime(),
});
export const exercisePreferenceListOutput = z.object({
    preferences: z.array(exercisePreferenceView),
});
/**
 * `PUT /me/exercise-preferences/:exerciseId`. A key left out is left as it
 * is, so the star and the chips are two independent writes that cannot undo
 * each other; `preference: null` clears the preference. A row whose two
 * values end neutral is deleted, not kept.
 */
export const putExercisePreferenceInput = z.strictObject({
    favorite: z.boolean().optional(),
    preference: exercisePreference.nullable().optional(),
});
// ── Goals ───────────────────────────────────────────────────────────────────
/** What a goal measures: the estimated one-rep max or the heaviest load. */
export const EXERCISE_GOAL_METRICS = ["e1rm", "load"];
export const exerciseGoalMetric = z.enum(EXERCISE_GOAL_METRICS);
/** Live goals one person may hold at once. Restated by `@repo/training`. */
export const MAX_ACTIVE_EXERCISE_GOALS = 30;
/** No lift anyone logs is heavier; a typo of 1000 for 100 is refused here. */
export const EXERCISE_GOAL_TARGET_MAX_KG = 1000;
/** The best the goal is measured against, and the set that made it. */
export const exerciseGoalCurrent = z.object({
    valueKg: z.number().nonnegative(),
    performedAt: z.iso.datetime(),
});
export const exerciseGoalView = z.object({
    goalId: z.string().min(1),
    exerciseId: z.string().min(1),
    /** In the request's locale. ABSENT when the catalogue has no such row. */
    name: z.string().min(1).optional(),
    metric: exerciseGoalMetric,
    targetKg: z.number().positive(),
    /** A calendar day, `YYYY-MM-DD`, or `null` for no date. */
    deadline: z.iso.date().nullable(),
    createdAt: z.iso.datetime(),
    /** Set by the first read that saw the target met, and never unset. */
    achievedAt: z.iso.datetime().nullable(),
    /** `null` while nothing counting toward this metric was logged. */
    current: exerciseGoalCurrent.nullable(),
});
/** The live goals, newest first. */
export const exerciseGoalListOutput = z.object({
    goals: z.array(exerciseGoalView),
});
/** `GET /me/exercise-goals?exerciseId&locale`. */
export const exerciseGoalListQuery = z.object({
    exerciseId: z.string().min(1).optional(),
});
/**
 * `POST /me/exercise-goals`. The id is minted on the device (`D163`), so a
 * resend after a lost answer is the same goal: 200 with the stored one
 * instead of 201 and a twin.
 */
export const createExerciseGoalInput = z.strictObject({
    id: z.uuid(),
    exerciseId: z.string().min(1),
    metric: exerciseGoalMetric,
    targetKg: z.number().positive().max(EXERCISE_GOAL_TARGET_MAX_KG),
    deadline: z.iso.date().nullable().optional(),
});
// ── Strength levels (`D208`) ────────────────────────────────────────────────
/**
 * The five levels, weakest first. ONE definition decides both the badge and
 * the table: a level is the band of the person's e1RM between the thresholds
 * the same answer carries, so the two can never disagree.
 */
export const STRENGTH_LEVELS = [
    "beginner",
    "novice",
    "intermediate",
    "advanced",
    "elite",
];
export const strengthLevel = z.enum(STRENGTH_LEVELS);
/** What the standard needs that the person has not given. */
export const STRENGTH_MISSING = ["dateOfBirth", "sex", "weight"];
export const strengthMissing = z.enum(STRENGTH_MISSING);
/** The population for this person's age, sex and weight, in kg of e1RM. */
export const strengthStandardView = z.object({
    /** The median lifter's estimated one-rep max. */
    meanKg: z.number(),
    /** One standard deviation, from the published 95 % band. */
    sdKg: z.number().positive(),
    /** Where each level BEGINS. Below `novice` is `beginner`. */
    thresholds: z.object({
        novice: z.number().nonnegative(),
        intermediate: z.number().nonnegative(),
        advanced: z.number().nonnegative(),
        elite: z.number().nonnegative(),
    }),
});
export const strengthLevelRow = z.object({
    exerciseId: z.string().min(1),
    name: z.string().min(1).optional(),
    standard: strengthStandardView,
    /** The person's best estimated 1RM (working sets), or `null` if never
     *  logged — the table is still worth showing. */
    best: z
        .object({
        e1rmKg: z.number().positive(),
        performedAt: z.iso.datetime(),
    })
        .nullable(),
    /** Of lifters of the same age, sex and weight; 0.1–99.9. */
    percentile: z.number().min(0).max(100).nullable(),
    level: strengthLevel.nullable(),
});
/** The facts the standard was evaluated at, so the copy can say so. */
export const strengthBasis = z.object({
    ageYears: z.int().nonnegative(),
    bodyWeightKg: z.number().positive(),
    sex: z.enum(["female", "male"]),
});
/** `GET /me/strength-levels` — every logged lift that has a standard. */
export const strengthLevelsOutput = z.object({
    /** Empty when the standard could be evaluated; else what to ask for. */
    missing: z.array(strengthMissing),
    basis: strengthBasis.nullable(),
    levels: z.array(strengthLevelRow),
});
/** `GET /me/strength-levels/:exerciseId`. `level: null` when this lift has
 *  no standard for the person's sex, or `missing` is not empty. */
export const strengthLevelOutput = z.object({
    missing: z.array(strengthMissing),
    basis: strengthBasis.nullable(),
    level: strengthLevelRow.nullable(),
});
//# sourceMappingURL=exercise-personal.js.map